import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { useAppHeader } from '../contexts/AppHeaderContext';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard, DashboardProvider } from '../contexts/DashboardContext';
import { PermissionProvider, usePermissions } from '../contexts/PermissionContext';
import GridDashboard from '../components/dashboard/GridDashboard';
import WidgetLibrary from '../components/dashboard/WidgetLibrary';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Settings,
  Save,
  RotateCcw,
  X,
  LayoutDashboard,
  BarChart2,
  Dumbbell,
  Sparkles,
  Share2,
  Shield,
  HeartPulse,
} from 'lucide-react';
import api from '../utils/axiosConfig';
import { cn } from '../lib/utils';
import { buildRoleOptionsFromContexts, roleWorkspaceLabel as roleWorkspaceLabelUtil } from '../utils/workspaceRoles';
import TrainingLogDock, { TRAINING_LOG_DOCK_VIEWPORT_GUTTER_PX } from '../components/trainingLog/TrainingLogDock';

function getContextNavPresentation(ctx, t) {
  const Icon =
    ctx.context_key === 'athlete_analysis'
      ? BarChart2
      : ctx.context_key === 'athlete_training_plans'
        ? Dumbbell
        : ctx.context_key === 'athlete_health'
          ? HeartPulse
          : ctx.context_key === 'zitius_ai_overview'
            ? Sparkles
            : ctx.context_key === 'admin_overview'
              ? Shield
              : ctx.context_key === 'coach_overview'
                ? LayoutDashboard
                : LayoutDashboard;
  const labelKey =
    ctx.context_key === 'athlete_analysis'
      ? 'dashboard.analysis'
      : ctx.context_key === 'athlete_overview'
        ? 'dashboard.overview'
        : ctx.context_key === 'athlete_training_plans'
          ? 'dashboard.trainingPlans'
          : ctx.context_key === 'athlete_health'
            ? 'dashboard.health'
            : ctx.context_key === 'zitius_ai_overview'
              ? 'dashboard.zitiusAi'
              : ctx.context_key === 'admin_overview'
                ? 'dashboard.adminOverview'
                : ctx.context_key === 'coach_overview'
                  ? 'dashboard.coachOverview'
                  : null;
  return { Icon, label: labelKey ? t(labelKey) : ctx.name };
}

const DashboardContent = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { fetchSettings } = useSettings();
  const { setHeaderCenter, setHeaderRight, setHeaderBackButton } = useAppHeader();

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    setHeaderBackButton(false);
    return () => setHeaderBackButton(false);
  }, [setHeaderBackButton]);
  const {
    currentContext, isLoading, error,
    isEditMode, hasUnsavedChanges,
    toggleEditMode, saveLayout, resetToTemplate,
    switchContext,
    templateStale,
    acknowledgeTemplateUpdate,
    publishRoleTemplate,
  } = useDashboard();

  const canPublishTemplate =
    Number(user?.id) === 1 || hasPermission('dashboard_template_publish');

  const [staleTemplateDialogOpen, setStaleTemplateDialogOpen] = useState(false);
  const [publishTemplateDialogOpen, setPublishTemplateDialogOpen] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);

  const [allAccessibleContexts, setAllAccessibleContexts] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [trainingLogInset, setTrainingLogInset] = useState(0);

  useEffect(() => {
    const stravaStatus = searchParams.get('strava');
    if (stravaStatus === 'connected') {
      setSearchParams({}, { replace: true });
    } else if (stravaStatus === 'error') {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const contextParamAppliedRef = useRef(null);
  useEffect(() => {
    const ck = searchParams.get('context');
    if (!ck) {
      contextParamAppliedRef.current = null;
      return;
    }
    if (!allAccessibleContexts.length || !switchContext) return;
    if (contextParamAppliedRef.current === ck) return;
    const ctx = allAccessibleContexts.find((c) => c.context_key === ck);
    if (!ctx) return;
    contextParamAppliedRef.current = ck;
    (async () => {
      try {
        await switchContext(ctx.context_id);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('context');
            return next;
          },
          { replace: true }
        );
      } catch (err) {
        console.error('Dashboard context query switch failed:', err);
        contextParamAppliedRef.current = null;
      }
    })();
  }, [searchParams, allAccessibleContexts, switchContext, setSearchParams]);

  useEffect(() => {
    if (!user?.id) return;
    api
      .get('/api/contexts/my-available')
      .then((res) => setAllAccessibleContexts(res.data?.contexts || []))
      .catch(() => setAllAccessibleContexts([]));
  }, [user?.id]);

  const roleOptions = useMemo(
    () => buildRoleOptionsFromContexts(allAccessibleContexts, currentContext),
    [allAccessibleContexts, currentContext]
  );

  const contextsForCurrentRole = useMemo(() => {
    if (!currentContext?.role_id) return [];
    return allAccessibleContexts.filter((c) => c.role_id === currentContext.role_id);
  }, [allAccessibleContexts, currentContext?.role_id]);

  const switchToRoleWorkspace = useCallback(
    (roleId) => {
      const group = allAccessibleContexts.filter((c) => Number(c.role_id) === Number(roleId));
      const def = group.find((c) => c.is_default) || group[0];
      if (def) switchContext(def.context_id);
    },
    [allAccessibleContexts, switchContext]
  );

  const roleWorkspaceLabel = useCallback((roleName) => roleWorkspaceLabelUtil(roleName, t), [t]);

  useEffect(() => {
    if (!templateStale) {
      setStaleTemplateDialogOpen(false);
      return;
    }
    if (!isLoading && currentContext) {
      setStaleTemplateDialogOpen(true);
    }
  }, [templateStale, isLoading, currentContext]);

  const handleStaleUseNewDefault = useCallback(async () => {
    setStaleTemplateDialogOpen(false);
    await resetToTemplate();
  }, [resetToTemplate]);

  const handleStaleKeepLayout = useCallback(async () => {
    setStaleTemplateDialogOpen(false);
    await acknowledgeTemplateUpdate();
  }, [acknowledgeTemplateUpdate]);

  const handleConfirmPublishTemplate = useCallback(async () => {
    setPublishBusy(true);
    try {
      await publishRoleTemplate();
      setPublishTemplateDialogOpen(false);
    } catch {
      /* error surfaced via dashboard context */
    } finally {
      setPublishBusy(false);
    }
  }, [publishRoleTemplate]);

  useEffect(() => {
    const openZitiusAi = async ({ detail }) => {
      const sessionId = detail?.sessionId;
      if (!sessionId) return;
      const ctx = allAccessibleContexts.find((c) => c.context_key === 'zitius_ai_overview');
      if (!ctx) return;
      try {
        await switchContext(ctx.context_id);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('agentSession', String(sessionId));
            return next;
          },
          { replace: true }
        );
      } catch (err) {
        console.error('Open ZitiusAI failed:', err);
      }
    };
    window.addEventListener('citius:openZitiusAi', openZitiusAi);
    return () => window.removeEventListener('citius:openZitiusAi', openZitiusAi);
  }, [allAccessibleContexts, switchContext, setSearchParams]);

  useEffect(() => {
    if (isLoading) {
      setHeaderCenter(null);
      setHeaderRight(null);
      return;
    }
    if (!currentContext) {
      setHeaderCenter(null);
      setHeaderRight(null);
      return;
    }
    setHeaderCenter(
      <div className='flex w-full max-w-full flex-nowrap items-center gap-2 min-[480px]:gap-3 min-w-0'>
        {roleOptions.length > 1 ? (
          <Select
            value={String(currentContext.role_id)}
            onValueChange={(v) => switchToRoleWorkspace(Number(v))}
          >
            <SelectTrigger
              className={cn(
                'h-9 w-[min(100%,260px)] max-w-[42vw] shrink-0 rounded-full border-border bg-muted/50 px-3.5 text-xs font-semibold shadow-sm',
                'ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-0',
                'data-[state=open]:ring-2 data-[state=open]:ring-ring'
              )}
              aria-label={t('dashboard.switchRole')}
            >
              <SelectValue placeholder={t('dashboard.switchRole')} />
            </SelectTrigger>
            <SelectContent align='start' className='min-w-[var(--radix-select-trigger-width)]'>
              {roleOptions.map((r) => (
                <SelectItem key={r.role_id} value={String(r.role_id)} className='text-xs font-medium'>
                  {roleWorkspaceLabel(r.role_name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant='secondary' className='h-7 shrink-0 px-2.5 text-xs'>
            {roleWorkspaceLabel(currentContext.role_name)}
          </Badge>
        )}
        {contextsForCurrentRole.length > 1 ? (
          <>
            <div
              className={cn(
                'hidden min-w-0 w-max max-w-full overflow-x-auto lg:flex lg:flex-nowrap lg:items-center',
                'rounded-lg border border-border p-0.5 [scrollbar-width:thin]'
              )}
            >
              {contextsForCurrentRole.map((ctx) => {
                const isActive = ctx.context_id === currentContext.context_id;
                const { Icon, label } = getContextNavPresentation(ctx, t);
                return (
                  <Button
                    key={ctx.context_id}
                    variant={isActive ? 'secondary' : 'ghost'}
                    size='sm'
                    className='h-7 shrink-0 gap-1.5 px-3'
                    onClick={() => !isActive && switchContext(ctx.context_id)}
                  >
                    <Icon className='h-3.5 w-3.5 shrink-0' />
                    {label}
                  </Button>
                );
              })}
            </div>
            <Select
              value={String(currentContext.context_id)}
              onValueChange={(v) => switchContext(Number(v))}
            >
              <SelectTrigger
                className={cn(
                  'h-9 flex-1 min-w-0 rounded-full border-border bg-muted/50 px-3.5 text-xs font-semibold shadow-sm lg:hidden',
                  'ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-0',
                  'data-[state=open]:ring-2 data-[state=open]:ring-ring'
                )}
                aria-label={t('dashboard.switchDashboard')}
              >
                <SelectValue placeholder={t('dashboard.switchDashboard')} />
              </SelectTrigger>
              <SelectContent align='start' className='min-w-[var(--radix-select-trigger-width)]'>
                {contextsForCurrentRole.map((ctx) => {
                  const { Icon, label } = getContextNavPresentation(ctx, t);
                  return (
                    <SelectItem
                      key={ctx.context_id}
                      value={String(ctx.context_id)}
                      className='text-xs font-medium'
                    >
                      <span className='flex items-center gap-2'>
                        <Icon className='h-3.5 w-3.5 shrink-0' />
                        {label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </>
        ) : (
          <span className='min-w-0 truncate text-sm text-muted-foreground'>{currentContext.name}</span>
        )}
      </div>
    );
    setHeaderRight(
      isEditMode ? (
        <>
          <Button variant='default' size='sm' onClick={saveLayout} disabled={!hasUnsavedChanges}>
            <Save className='h-4 w-4 mr-1' />
            {t('dashboard.saveChanges')}
          </Button>
          {canPublishTemplate && (
            <Button
              variant='secondary'
              size='sm'
              onClick={() => setPublishTemplateDialogOpen(true)}
            >
              <Share2 className='h-4 w-4 mr-1' />
              {t('dashboard.publishAsRoleDefault')}
            </Button>
          )}
          <Button variant='outline' size='sm' onClick={resetToTemplate}>
            <RotateCcw className='h-4 w-4 mr-1' />
            {t('dashboard.resetLayout')}
          </Button>
          <Button variant='ghost' size='sm' onClick={toggleEditMode}>
            <X className='h-4 w-4 mr-1' />
            {t('dashboard.cancelEdit')}
          </Button>
        </>
      ) : (
        <Button variant='outline' size='icon' className='h-8 w-8' onClick={toggleEditMode} title={t('dashboard.editMode')}>
          <Settings className='h-4 w-4' />
        </Button>
      )
    );
    return () => {
      setHeaderCenter(null);
      setHeaderRight(null);
    };
  }, [
    isLoading,
    currentContext,
    roleOptions,
    contextsForCurrentRole,
    switchToRoleWorkspace,
    roleWorkspaceLabel,
    isEditMode,
    hasUnsavedChanges,
    switchContext,
    saveLayout,
    resetToTemplate,
    toggleEditMode,
    t,
    setHeaderCenter,
    setHeaderRight,
    canPublishTemplate,
  ]);

  const showTrainingLog =
    currentContext?.role_name?.toLowerCase() === 'athlete' &&
    currentContext?.context_key === 'athlete_overview';
  const trainingLogVisible = showTrainingLog && !isEditMode;

  useEffect(() => {
    if (!trainingLogVisible) setTrainingLogInset(0);
  }, [trainingLogVisible]);

  if (isLoading) {
    return (
      <div className='p-6 space-y-4'>
        <div className='grid grid-cols-12 gap-4'>
          <Skeleton className='col-span-6 h-64 rounded-lg' />
          <Skeleton className='col-span-6 h-64 rounded-lg' />
          <Skeleton className='col-span-12 h-80 rounded-lg' />
        </div>
      </div>
    );
  }

  return (
    <div className='citius-screenshot-dashboard-page flex'>
      <Dialog open={staleTemplateDialogOpen} onOpenChange={() => {}}>
        <DialogContent
          className='max-w-[min(90vw,28rem)]'
          showCloseButton={false}
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          priority='high'
        >
          <DialogHeader>
            <DialogTitle>{t('dashboard.templateUpdatedTitle')}</DialogTitle>
            <DialogDescription>{t('dashboard.templateUpdatedDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button type='button' variant='outline' onClick={handleStaleKeepLayout}>
              {t('dashboard.keepMyLayout')}
            </Button>
            <Button type='button' onClick={handleStaleUseNewDefault}>
              {t('dashboard.useNewDefaultLayout')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={publishTemplateDialogOpen} onOpenChange={setPublishTemplateDialogOpen}>
        <DialogContent className='max-w-[min(90vw,28rem)]' priority='high'>
          <DialogHeader>
            <DialogTitle>{t('dashboard.publishTemplateTitle')}</DialogTitle>
            <DialogDescription>{t('dashboard.publishTemplateDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setPublishTemplateDialogOpen(false)}
              disabled={publishBusy}
            >
              {t('dashboard.cancelEdit')}
            </Button>
            <Button type='button' onClick={handleConfirmPublishTemplate} disabled={publishBusy}>
              {t('dashboard.publishTemplateConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div
        className={cn(
          'flex-1 flex flex-col pb-6 min-w-0 pt-4 pl-6 transition-[margin] duration-300 ease-out',
          trainingLogVisible ? 'pr-4' : 'pr-6'
        )}
        style={{
          marginRight: trainingLogVisible
            ? trainingLogInset + TRAINING_LOG_DOCK_VIEWPORT_GUTTER_PX
            : 0,
        }}
      >
        {error ? (
          <div className='flex items-center justify-center h-64'>
            <div className='text-center'>
              <p className='text-destructive font-medium'>{error}</p>
              <Button variant='outline' className='mt-4' onClick={() => window.location.reload()}>
                {t('common.back')}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <GridDashboard />
          </div>
        )}
      </div>

      {isEditMode && <WidgetLibrary />}
      {trainingLogVisible && (
        <TrainingLogDock onInsetChange={setTrainingLogInset} />
      )}
    </div>
  );
};

const Dashboard = () => {
  return (
    <PermissionProvider>
      <DashboardProvider>
        <DashboardContent />
      </DashboardProvider>
    </PermissionProvider>
  );
};

export default Dashboard;
